import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, DeviceLimitError } from "../context/AuthContext";
import { DeviceLimitDialog } from "../components/DeviceSession";
import "../assets/styles/login.css";
import logo from "../assets/images/logo.png";

const REMEMBER_KEY = "adminRememberCredentials";

const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.message ||
  "Login failed. Please try again.";

const Login = () => {
  const navigate = useNavigate();
  const { login, resolveLogin } = useAuth();
  const [deviceLimit, setDeviceLimit] = useState(null);

  const [form, setForm] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        setForm({
          email: parsed.email || "",
          rememberMe: true,
        });
      } catch {
        localStorage.removeItem(REMEMBER_KEY);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRememberCredentials = () => {
    if (form.rememberMe) {
      localStorage.setItem(
        REMEMBER_KEY,
        JSON.stringify({
          email: form.email,
        })
      );
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      await login({
        email: form.email,
        password: form.password,
      });

      finishLogin();
    } catch (err) {
      if (err instanceof DeviceLimitError) {
        setDeviceLimit(err.data);
        return;
      }

      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  function finishLogin() {
    handleRememberCredentials();
    navigate("/master_admin_panel/");
  }

  const handleResolveDeviceLimit = async (revokeSessionUids) => {
    try {
      await resolveLogin({
        challengeToken: deviceLimit.challenge_token,
        revokeSessionUids,
      });

      setDeviceLimit(null);
      finishLogin();
    } catch (err) {
      if (err instanceof DeviceLimitError) {
        // Another device signed in meanwhile; show the refreshed list.
        setDeviceLimit(err.data);
        return;
      }

      setDeviceLimit(null);
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <section className="admin-login-brand">
          <div className="admin-login-logo"><img src={logo} alt="logo" className="logo" /></div>

          <p>Weddings Stationary</p>

          <h1>Welcome back, Admin</h1>

          <span>
            Manage products, categories, bulk uploads, forms and custom wedding
            stationery orders from one premium dashboard.
          </span>
        </section>

        <section className="admin-login-panel">
          <div className="admin-login-heading">
            <h2>Sign in</h2>
            <p>Enter your admin credentials to continue.</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form">
            {error && <div className="admin-login-error">{error}</div>}

            <label>
              Email Address
              <input
                type="email"
                name="email"
                placeholder="admin@example.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Password
              <div className="admin-password-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  value={form.password}
                  onChange={handleChange}
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "Hide" : "View"}
                </button>
              </div>
            </label>

            <div className="admin-login-options">
              <label className="admin-remember">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={form.rememberMe}
                  onChange={handleChange}
                />
                Remember email & password
              </label>

              <span>Admin only</span>
            </div>

            <button
              type="submit"
              className="admin-login-submit"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </section>
      </div>

      <DeviceLimitDialog
        visible={Boolean(deviceLimit)}
        limitData={deviceLimit}
        onCancel={() => setDeviceLimit(null)}
        onConfirm={handleResolveDeviceLimit}
      />
    </div>
  );
};

export default Login;